import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Grid, Chip, Paper } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import GoldIcon from "../assets/icons/gold.png";
import { updateStudentCoins } from "../utils/coin";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarsIcon from "@mui/icons-material/Stars";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShieldIcon from "@mui/icons-material/Shield";
import BoltIcon from "@mui/icons-material/Bolt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { toast } from "react-toastify";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#F59E0B", light: "#FEF3C7", dark: "#D97706" },
    background: { default: "#FFFBF0", paper: "#ffffff" },
    text: { primary: "#0c1a2e", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: { fontFamily: "'Bricolage Grotesque', 'Sora', sans-serif" },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const coinFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50%       { transform: translateY(-10px) rotate(8deg); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(245,158,11,0.3), 0 0 40px rgba(245,158,11,0.1); }
  50%       { box-shadow: 0 0 30px rgba(245,158,11,0.5), 0 0 60px rgba(245,158,11,0.2); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background:
    "linear-gradient(160deg, #FFFBF0 0%, #FEF3C7 30%, #FFF8E7 60%, #FFFBF5 100%)",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -100,
    right: -100,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const HeroBar = styled(Box)(() => ({
  background:
    "linear-gradient(135deg, #92400E 0%, #D97706 40%, #F59E0B 70%, #FCD34D 100%)",
  padding: "72px 24px 110px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    opacity: 0.06,
    backgroundImage:
      "radial-gradient(circle at 25% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 50%, #fff 1px, transparent 1px)",
    backgroundSize: "50px 50px",
  },
}));

const HeroBackBtn = styled(Box)(() => ({
  position: "absolute",
  left: 24,
  top: "50%",
  transform: "translateY(-50%)",
  width: 42,
  height: 42,
  borderRadius: "12px",
  background: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.28)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "#fff",
  zIndex: 10,
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.28)",
    transform: "translateY(-50%) translateX(-2px)",
  },
}));

const BalanceCard = styled(Paper)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 20,
  background: "#fff",
  borderRadius: 24,
  padding: "28px 48px",
  border: `3px solid ${alpha("#F59E0B", 0.4)}`,
  boxShadow: "0 8px 32px rgba(245,158,11,0.15), 0 2px 8px rgba(0,0,0,0.06)",
  animation: `${glowPulse} 3s ease-in-out infinite`,
  marginTop: -48,
  position: "relative",
  zIndex: 2,
}));

const CoinImg = styled("img")(() => ({
  width: 56,
  height: 56,
  animation: `${coinFloat} 3s ease-in-out infinite`,
  filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
}));

const PackageCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "isPopular" && p !== "itemdelay",
})(({ isPopular, itemdelay }) => ({
  borderRadius: 22,
  overflow: "hidden",
  border: isPopular ? "3px solid #F59E0B" : `1px solid ${alpha("#000", 0.08)}`,
  boxShadow: isPopular
    ? "0 12px 40px rgba(245,158,11,0.2), 0 4px 12px rgba(0,0,0,0.08)"
    : "0 2px 8px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  cursor: "pointer",
  transform: isPopular ? "scale(1.04)" : "scale(1)",
  zIndex: isPopular ? 2 : 1,
  animation: `${cardEntrance} 0.5s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  background: "#fff",
  "&:hover": {
    transform: isPopular
      ? "scale(1.07) translateY(-6px)"
      : "scale(1.02) translateY(-8px)",
    boxShadow: isPopular
      ? "0 20px 56px rgba(245,158,11,0.28), 0 8px 20px rgba(0,0,0,0.1)"
      : "0 16px 44px rgba(245,158,11,0.15), 0 6px 16px rgba(0,0,0,0.08)",
  },
}));

const CardHeroSection = styled(Box, {
  shouldForwardProp: (p) => p !== "isPopular",
})(({ isPopular }) => ({
  background: isPopular
    ? "linear-gradient(135deg, #92400E 0%, #D97706 50%, #F59E0B 100%)"
    : "linear-gradient(135deg, #1E3A5F 0%, #0288D1 100%)",
  padding: "36px 24px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    opacity: 0.06,
    backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
    backgroundSize: "30px 30px",
  },
}));

const BuyBtn = styled(Box, {
  shouldForwardProp: (p) => p !== "isPopular",
})(({ isPopular }) => ({
  width: "100%",
  padding: "14px 20px",
  borderRadius: 12,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontSize: 15,
  fontWeight: 800,
  letterSpacing: "0.2px",
  background: isPopular
    ? "linear-gradient(135deg, #D97706, #F59E0B)"
    : "linear-gradient(135deg, #0277bd, #0288D1)",
  color: "#fff",
  boxShadow: isPopular
    ? "0 4px 16px rgba(245,158,11,0.35)"
    : "0 4px 16px rgba(2,136,209,0.3)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: isPopular
      ? "0 6px 22px rgba(245,158,11,0.45)"
      : "0 6px 22px rgba(2,136,209,0.4)",
  },
  "&:active": { transform: "translateY(0)" },
}));



const SkeletonCard = styled(Box)(() => ({
  borderRadius: 22,
  border: `1px solid ${alpha("#000", 0.07)}`,
  overflow: "hidden",
  background: "#fff",
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const Sb = ({ w = "100%", h = 14, r = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: r,
      background: alpha("#000", 0.08),
      flexShrink: 0,
    }}
  />
);

const TrustBadge = ({ icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
    {icon}
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: "#9CA3AF",
        fontFamily: "'Bricolage Grotesque', sans-serif",
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const GoldPage = () => {
  const navigate = useNavigate();
  const [numOfCoin, setNumOfCoin] = useState(0);
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [email, setEmail] = useState("");
  const [publickey, setPublickey] = useState("");
  const [secretkey, setSecretkey] = useState("");

  useEffect(() => {
    const refreshCoins = async () => {
      await updateStudentCoins();
    };
    refreshCoins();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenString = localStorage.getItem("authToken");
        if (!tokenString) throw new Error("No token");
        const token = JSON.parse(tokenString);
        const [coinsRes, profileRes] = await Promise.all([
          axios.get(`${API_URL}/coins/`, {
            headers: { Authorization: `Bearer ${token.access}` },
          }),
          axios.get(`${API_URL}/student/profile/`, {
            headers: { Authorization: `Bearer ${token.access}` },
          }),
        ]);
        setCoins(coinsRes.data);
        setEmail(profileRes.data.user.email);
        setPublickey(profileRes.data.publickey);
        setSecretkey(profileRes.data.secretkey);
        setNumOfCoin(profileRes.data.coin_num || 0);
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCoinPress = (item) => {
    if (!email) {
      toast.error("Unable to get your email. Please try again.");
      return;
    }
    navigate("/paystack-payment", {
      state: {
        amount: item.amount,
        coins: item.coins,
        email,
        secretkey,
        publickey,
      },
    });
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(false);
    window.location.reload();
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        {/* ── Hero ── */}
        <HeroBar>
          <HeroBackBtn onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </HeroBackBtn>

          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Chip
              label="💰 Coin Store"
              size="small"
              sx={{
                mb: 2.5,
                fontWeight: 700,
                fontSize: "0.74rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: "0.4px",
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.28)",
                borderRadius: "8px",
                backdropFilter: "blur(8px)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: "#fff",
                fontSize: { xs: "2.6rem", md: "4rem" },
                letterSpacing: "-1.5px",
                lineHeight: 1.08,
                fontFamily: "'Bricolage Grotesque', sans-serif",
                mb: 1.5,
              }}
            >
              Buy Coins
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.75)",
                fontSize: "1.05rem",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontWeight: 400,
              }}
            >
              Unlock premium features &amp; practice more!
            </Typography>
          </Box>
        </HeroBar>

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 4, md: 8 },
            mt: { xs: -3, md: -4 },
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* ── Balance Card ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 8,
              animation: `${fadeUp} .5s ease both`,
            }}
          >
            <BalanceCard elevation={0}>
              <CoinImg src={GoldIcon} alt="Gold" />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: "#9CA3AF",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    mb: 0.3,
                  }}
                >
                  Your Balance
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: "2.6rem",
                      letterSpacing: "-1px",
                      lineHeight: 1,
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      background:
                        "linear-gradient(135deg, #D97706, #F59E0B, #FCD34D)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {numOfCoin.toLocaleString()}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.1rem",
                      color: "#D97706",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                    }}
                  >
                    Coins
                  </Typography>
                </Box>
              </Box>
            </BalanceCard>
          </Box>

          {/* ── Loading ── */}
          {loading && (
            <Grid container spacing={3} justifyContent="center">
              {[...Array(3)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <SkeletonCard sx={{ animationDelay: `${i * 90}ms` }}>
                    <Box
                      sx={{ height: 200, background: alpha("#F59E0B", 0.07) }}
                    />
                    <Box
                      sx={{
                        p: "24px 22px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <Sb w="50%" h={28} r={6} />
                      <Sb w="35%" h={16} r={5} />
                      <Box sx={{ mt: 1 }}>
                        <Sb w="100%" h={48} r={12} />
                      </Box>
                    </Box>
                  </SkeletonCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* ── Error ── */}
          {error && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                animation: `${fadeUp} .4s ease both`,
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 3,
                  background: alpha("#DC2626", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <RefreshIcon
                  sx={{ fontSize: 28, color: "#DC2626", opacity: 0.6 }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#0c1a2e",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  mb: 0.5,
                }}
              >
                Failed to load packages
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  mb: 2.5,
                }}
              >
                Check your connection and try again
              </Typography>
              <Box
                onClick={handleRefresh}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 3,
                  py: 1.5,
                  borderRadius: "10px",
                  background: alpha("#DC2626", 0.08),
                  border: `1px solid ${alpha("#DC2626", 0.2)}`,
                  color: "#DC2626",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": { background: alpha("#DC2626", 0.14) },
                }}
              >
                <RefreshIcon sx={{ fontSize: 16 }} /> Retry
              </Box>
            </Box>
          )}

          {/* ── Packages ── */}
          {!loading && !error && (
            <>
              <Box
                sx={{
                  textAlign: "center",
                  mb: 6,
                  animation: `${fadeUp} .45s ease both`,
                  animationDelay: "60ms",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: "#D97706",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    mb: 1,
                  }}
                >
                  Available Packages
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#0c1a2e",
                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                    letterSpacing: "-0.8px",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Choose Your Package
                </Typography>
              </Box>

              <Grid
                container
                spacing={3}
                justifyContent="center"
                alignItems="flex-end"
              >
                {coins.map((item, index) => {
                  const isPopular = index === 1;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <PackageCard
                        isPopular={isPopular}
                        itemdelay={index * 80}
                        elevation={0}
                        onClick={() => handleCoinPress(item)}
                      >
                        {/* Popular badge */}
                        {isPopular && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: "50%",
                              transform: "translateX(-50%)",
                              zIndex: 10,
                              background:
                                "linear-gradient(135deg, #D97706, #F59E0B)",
                              color: "#fff",
                              px: 2.5,
                              py: 0.7,
                              borderRadius: "0 0 10px 10px",
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: "0.6px",
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                              textTransform: "uppercase",
                              boxShadow: "0 4px 12px rgba(245,158,11,0.4)",
                            }}
                          >
                            ⭐ Most Popular
                          </Box>
                        )}

                        <CardHeroSection
                          isPopular={isPopular}
                          sx={{ pt: isPopular ? 6 : 4 }}
                        >
                          <Box sx={{ position: "relative", zIndex: 1 }}>
                            <img
                              src={GoldIcon}
                              alt="Coins"
                              style={{
                                width: 80,
                                height: 80,
                                margin: "0 auto",
                                display: "block",
                                filter:
                                  "drop-shadow(0 6px 12px rgba(0,0,0,0.2))",
                                animation: isPopular
                                  ? `${coinFloat} 3s ease-in-out infinite`
                                  : "none",
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 900,
                                color: "#fff",
                                fontSize: "1.9rem",
                                letterSpacing: "-0.5px",
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                mt: 2,
                                lineHeight: 1,
                              }}
                            >
                              {item.coins.toLocaleString()}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.75)",
                                fontSize: "0.9rem",
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                              }}
                            >
                              Coins
                            </Typography>
                          </Box>
                        </CardHeroSection>

                        <Box sx={{ p: "22px 22px 24px", textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: "#0c1a2e",
                              fontSize: "1.8rem",
                              letterSpacing: "-0.5px",
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                              lineHeight: 1,
                            }}
                          >
                            ₦{item.amount.toLocaleString()}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#9CA3AF",
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                              fontWeight: 500,
                              mt: 0.5,
                              mb: 2.5,
                            }}
                          >
                            ≈ ₦{(item.amount / item.coins).toFixed(2)} per coin
                          </Typography>
                          <BuyBtn isPopular={isPopular}>
                            <StarsIcon sx={{ fontSize: 17 }} />
                            Buy Now
                          </BuyBtn>
                        </Box>
                      </PackageCard>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* ── Trust badges ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 3, md: 5 },
              mt: 6,
              flexWrap: "wrap",
              animation: `${fadeUp} .5s ease both`,
              animationDelay: "300ms",
            }}
          >
            <TrustBadge
              icon={<ShieldIcon sx={{ fontSize: 15, color: "#059669" }} />}
              label="Secure payments"
            />
            <TrustBadge
              icon={<BoltIcon sx={{ fontSize: 15, color: "#F59E0B" }} />}
              label="Instant delivery"
            />
            <TrustBadge
              icon={
                <SupportAgentIcon sx={{ fontSize: 15, color: "#0288D1" }} />
              }
              label="24/7 support"
            />
          </Box>
        </Container>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default GoldPage;
